// Discord Embed Preview Component
function DiscordEmbedPreview({
  title,
  description,
  color,
  thumbnail,
  image,
  footer,
  username,
  serverIcon
}: {
  title?: string,
  description?: string,
  color?: string,
  thumbnail?: string,
  image?: string,
  footer?: string,
  username?: string,
  serverIcon?: string
}) {
  return (
    <div className="rounded-lg border border-gray-700 bg-[#313338] p-4 max-w-xl mx-auto mt-4 shadow-lg">
      <div className="flex items-center mb-2">
        {thumbnail && thumbnail !== 'none' && (
          <img src={thumbnail} alt="thumb" className="w-10 h-10 rounded-full mr-3 border border-gray-700 bg-black object-cover" />
        )}
        <span className="font-semibold text-white text-base">{username || 'Bot'}</span>
      </div>
      <div className="border-l-4 pl-3" style={{ borderColor: color || '#5865F2' }}>
        {title && <div className="font-bold text-white text-lg mb-1">{title}</div>}
        {description && <div className="text-gray-200 whitespace-pre-line mb-2">{description}</div>}
        {image && (
          <img src={image} alt="banner" className="rounded-lg border border-gray-700 max-h-40 object-cover w-full mb-2" />
        )}
        {footer && <div className="text-xs text-gray-400 pt-2">{footer}</div>}
      </div>
    </div>
  );
}
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { serverApi, pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, MessageSquare, UserPlus, LogOut, ShieldCheck, AlertCircle, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';

export function WelcomePlugin({ serverId }: { serverId: string }) {
    const { t } = useI18n();
  // ── Refs separati ────────────────────────────────────────────────
  const welcomeFileInputRef = useRef<HTMLInputElement>(null);
  const farewellFileInputRef = useRef<HTMLInputElement>(null);

  // ── Stati di caricamento separati ───────────────────────────────
  const [uploadingWelcome, setUploadingWelcome] = useState(false);
  const [uploadingFarewell, setUploadingFarewell] = useState(false);

  // ── Preview immagini ────────────────────────────────────────────
  const [welcomeBannerPreview, setWelcomeBannerPreview] = useState('');
  const [farewellBannerPreview, setFarewellBannerPreview] = useState('');

  // ── Configurazione completa ─────────────────────────────────────
  const [config, setConfig] = useState<any>({
    // Welcome
    channelId: '',
    sendMessage: true,
    message: 'Welcome to {server}, {user}!',
    sendEmbed: false,
    embedTitle: 'Welcome!',
    embedColor: '#5865F2',
    embedThumbnail: 'user',
    embedShowBanner: false,
    bannerImageUrl: '',

    welcomeRoleId: 'none',

    // Farewell
    farewellEnabled: false,
    farewellChannelId: '',
    farewellMessage: '{user} has left {server}.',
    farewellSendEmbed: false,
    farewellEmbedTitle: 'Goodbye!',
    farewellEmbedColor: '#FF5555',
    farewellEmbedThumbnail: 'user',
    farewellEmbedShowBanner: false,
    farewellBannerImageUrl: '',

  });

  // ── Query ────────────────────────────────────────────────────────
  const { data: plugins, isLoading: pluginsLoading, error: pluginsError } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
    retry: 2,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
    retry: 2,
  });

  const welcomePlugin = (plugins as any[])?.find((p: any) => p.pluginId === 'welcome');
  const isEnabled = welcomePlugin?.enabled ?? false;

  // ── Mutations ────────────────────────────────────────────────────
  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'welcome') : pluginApi.disablePlugin(serverId, 'welcome'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving, isError: configError } = useApiMutation(
    (cfg: any) => pluginApi.updatePluginConfig(serverId, 'welcome', cfg),
    {
      onSuccess: () => toast.success(t('verification.savedToast')),
      onError: (err: any) => {
        toast.error(err?.message || 'Errore durante il salvataggio');
        console.error('Errore salvataggio config:', err);
      },
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  // ── Carica configurazione esistente ──────────────────────────────
  useEffect(() => {
    if (welcomePlugin?.config) {
      setConfig((prev: any) => ({
        ...prev,
        ...welcomePlugin.config,
      }));
    }
  }, [welcomePlugin]);

  // ── Imposta preview immagini all'avvio ───────────────────────────
  // Utility per banner URL assoluta
  function getAbsoluteBannerUrl(url: string | undefined): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) {
      // Usa dominio corrente (frontend) oppure variabile di ambiente se disponibile
      const base = import.meta.env.VITE_PUBLIC_URL || window.location.origin;
      return base.replace(/\/$/, '') + url;
    }
    return url;
  }

  useEffect(() => {
    setWelcomeBannerPreview(getAbsoluteBannerUrl(config.customBannerUrl || config.bannerImageUrl));
  }, [config.customBannerUrl, config.bannerImageUrl]);

  useEffect(() => {
    setFarewellBannerPreview(getAbsoluteBannerUrl(config.farewellCustomBannerUrl || config.farewellBannerImageUrl));
  }, [config.farewellCustomBannerUrl, config.farewellBannerImageUrl]);

  // ── Upload banner welcome ───────────────────────────────────────
  const handleWelcomeBannerUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 5MB)');
      return;
    }

    setUploadingWelcome(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('serverId', serverId);

      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${apiUrl}/api/upload/banner`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) throw new Error('Upload fallito');

      const data = await res.json();
      const url = data.url;

      setConfig((prev: any) => {
        const newConfig = { ...prev, bannerImageUrl: url, customBannerUrl: url };
        setWelcomeBannerPreview(url);
        toast.success('Banner caricato');
        // Salva subito la configurazione per evitare dimenticanze
        updateConfig(newConfig);
        return newConfig;
      });
    } catch (err) {
      console.error(err);
      toast.error('Errore durante il caricamento del banner');
    } finally {
      setUploadingWelcome(false);
      if (welcomeFileInputRef.current) welcomeFileInputRef.current.value = '';
    }
  };

  // ── Upload banner farewell ──────────────────────────────────────
  const handleFarewellBannerUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 5MB)');
      return;
    }

    setUploadingFarewell(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('serverId', serverId);

      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');

      const res = await fetch(`${apiUrl}/api/upload/banner`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) throw new Error('Upload fallito');

      const data = await res.json();
      const url = data.url;

      setConfig((prev: any) => {
        const newConfig = { ...prev, farewellBannerImageUrl: url, farewellCustomBannerUrl: url };
        setFarewellBannerPreview(url);
        toast.success('Banner di addio caricato');
        // Salva subito la configurazione per evitare dimenticanze
        updateConfig(newConfig);
        return newConfig;
      });
    } catch (err) {
      console.error(err);
      toast.error('Errore durante il caricamento del banner');
    } finally {
      setUploadingFarewell(false);
      if (farewellFileInputRef.current) farewellFileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    updateConfig(config);
  };

  // ── Rendering ────────────────────────────────────────────────────
  if (pluginsError) {
    return (
      <div className="flex items-center justify-center h-64 text-center space-y-4">
        <div>
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h3 className="font-bold text-lg mt-4">{t('welcome.loadError')}</h3>
          <p className="text-sm text-muted-foreground mt-2">
            {pluginsError instanceof Error ? pluginsError.message : t('welcome.unableLoad')}
          </p>
          <Button size="sm" className="mt-4" onClick={() => window.location.reload()}>
            {t('servers.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (pluginsLoading || !welcomePlugin) {
    return (
      <div className="flex items-center justify-center h-64 text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">{t('welcome.loadingConfig')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-500/20 rounded-xl">
            <UserPlus className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">{t('welcome.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('welcome.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="plugin-toggle" className="cursor-pointer">{t('welcome.pluginStatus')}</Label>
          <Switch
            id="plugin-toggle"
            checked={isEnabled}
            onCheckedChange={(checked) => togglePlugin({ enabled: checked })}
          />
        </div>
      </div>

      {!isEnabled && (
        <div className="glass-card p-6 bg-yellow-500/10 border-yellow-500/20 border">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-yellow-400" />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-400">{t('welcome.disabled')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('welcome.disabledDesc')}
              </p>
            </div>
            <Button size="sm" onClick={() => togglePlugin({ enabled: true })}>
              {t('welcome.enableNow')}
            </Button>
          </div>
        </div>
      )}

      {configError && (
        <div className="glass-card p-6 bg-red-500/10 border-red-500/20 border">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div className="flex-1">
              <h4 className="font-medium text-red-400">{t('welcome.saveFailed')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('welcome.saveFailedDesc')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={cn('space-y-8 transition-opacity duration-300', !isEnabled && 'opacity-50 pointer-events-none')}>

        {/* Welcome Section */}
        <section className="glass-card overflow-hidden">
          <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-400" />
            <h4 className="font-semibold">{t('welcome.welcomeMessage')}</h4>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <Label className="text-base">{t('welcome.sendOnJoin')}</Label>
              <Switch
                checked={config.sendMessage}
                onCheckedChange={(v) => setConfig({ ...config, sendMessage: v })}
              />
            </div>

            {config.sendMessage && (
              <div className="space-y-6 pt-2 animate-in fade-in slide-in-from-top-2">
                {/* Canale e Ruolo automatico */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t('welcome.channel')}</Label>
                    <Select
                      value={config.channelId}
                      onValueChange={(v) => setConfig({ ...config, channelId: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('accepted.selectChannel')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="system">{t('welcome.systemChannel')}</SelectItem>
                        {channels.map((ch: any) => (
                          <SelectItem key={ch.id} value={ch.id}>#{ch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('welcome.autoRole')}</Label>
                    <Select
                      value={config.welcomeRoleId || 'none'}
                      onValueChange={(v) => setConfig({ ...config, welcomeRoleId: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('accepted.selectRole')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('welcome.none')}</SelectItem>
                        {roles.map((role: any) => (
                          <SelectItem key={role.id} value={role.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: role.color || '#99aab5' }} />
                              {role.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Contenuto messaggio */}
                <div className="space-y-2">
                  <Label>{t('welcome.messageContent')}</Label>
                  <Textarea
                    value={config.message}
                    onChange={(e) => setConfig({ ...config, message: e.target.value })}
                    placeholder="Benvenuto {user} su {server}!"
                    rows={4}
                    className="bg-black/20 font-mono text-sm"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['{user}', '{username}', '{server}', '{memberCount}'].map((tag) => (
                      <code
                        key={tag}
                        className="text-[10px] bg-indigo-500/10 text-indigo-400 px-1.5 py-0.5 rounded border border-indigo-500/20"
                      >
                        {tag}
                      </code>
                    ))}
                  </div>
                </div>
                {/* Invia come Embed */}
                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <Label>{t('welcome.sendAsEmbed')}</Label>
                  <Switch
                    checked={config.sendEmbed}
                    onCheckedChange={(v) => setConfig({ ...config, sendEmbed: v })}
                  />
                </div>
                {/* Sezione Embed */}
                {config.sendEmbed && (
                  <>
                    {/* Discord Embed Preview Welcome */}
                    <DiscordEmbedPreview
                      title={config.embedTitle}
                      description={config.message.replace('{user}', 'User#1234').replace('{username}', 'User').replace('{server}', 'Server').replace('{memberCount}', '123')}
                      color={config.embedColor}
                      thumbnail={
                        config.embedThumbnail === 'server'
                          ? '/server-icon.png'
                          : config.embedThumbnail === 'user'
                            ? '/user-avatar.png'
                            : undefined
                      }
                      image={config.embedShowBanner && welcomeBannerPreview ? welcomeBannerPreview : undefined}
                      footer={`You are member #123`}
                      username="Bot"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label>{t('welcome.embedTitle')}</Label>
                        <Input
                          value={config.embedTitle}
                          onChange={(e) => setConfig({ ...config, embedTitle: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('welcome.embedColor')}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={config.embedColor}
                            onChange={(e) => setConfig({ ...config, embedColor: e.target.value })}
                            className="w-12 h-10 p-1 bg-transparent border-0"
                          />
                          <Input
                            value={config.embedColor}
                            onChange={(e) => setConfig({ ...config, embedColor: e.target.value })}
                            className="font-mono"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('welcome.embedThumb')}</Label>
                        <Select
                          value={config.embedThumbnail}
                          onValueChange={(v) => setConfig({ ...config, embedThumbnail: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Miniatura" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">{t('welcome.userAvatar')}</SelectItem>
                            <SelectItem value="server">{t('welcome.serverIcon')}</SelectItem>
                            <SelectItem value="none">{t('welcome.noThumb')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('welcome.embedBanner')}</Label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">{t('welcome.showServerBanner')}</span>
                          <Switch
                            checked={!!config.embedShowBanner}
                            onCheckedChange={(v) => setConfig({ ...config, embedShowBanner: v })}
                          />
                        </div>
                      </div>
                      {/* Upload banner welcome */}
                      <div className="md:col-span-2 space-y-3 p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                        <Label className="flex items-center gap-2 text-indigo-400">
                          <Upload className="w-4 h-4" />
                          {t('welcome.customBanner')}
                        </Label>
                        {(config.embedShowBanner && welcomeBannerPreview) ? (
                          <>
                            <div className="text-xs text-yellow-500 mt-1">{t('welcome.bannerReminder')}</div>
                            <div className="relative group">
                              <img
                                src={welcomeBannerPreview}
                                alt="Banner anteprima"
                                className="w-full h-32 object-cover rounded-lg border border-white/20"
                                onError={e => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  const fallback = document.getElementById('welcome-banner-fallback');
                                  if (fallback) fallback.style.display = 'block';
                                }}
                              />
                              <div id="welcome-banner-fallback" style={{display:'none'}} className="w-full h-32 flex items-center justify-center bg-red-100 text-red-500 rounded-lg border border-red-300">
                                {t('welcome.bannerError')}
                              </div>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => welcomeFileInputRef.current?.click()}
                                  disabled={uploadingWelcome}
                                >
                                  {t('welcome.change')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setWelcomeBannerPreview('');
                                    setConfig((p: any) => ({ ...p, bannerImageUrl: '' }));
                                    const fallback = document.getElementById('welcome-banner-fallback');
                                    if (fallback) fallback.style.display = 'none';
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => welcomeFileInputRef.current?.click()}
                            disabled={uploadingWelcome}
                            className="w-full p-6 border-2 border-dashed border-indigo-500/30 rounded-lg hover:bg-indigo-500/10 transition-colors cursor-pointer text-center"
                          >
                            <ImageIcon className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-indigo-400">
                              {uploadingWelcome ? t('welcome.uploading') : t('welcome.uploadBanner')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('welcome.uploadHint')}
                            </p>
                          </button>
                        )}
                        <input
                          ref={welcomeFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleWelcomeBannerUpload(file);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Farewell Section */}
        <section className="glass-card overflow-hidden">
          <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
            <LogOut className="w-5 h-5 text-red-400" />
            <h4 className="font-semibold">{t('welcome.farewellMessage')}</h4>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <Label className="text-base">{t('welcome.sendOnLeave')}</Label>
              <Switch
                checked={config.farewellEnabled}
                onCheckedChange={(v) => setConfig({ ...config, farewellEnabled: v })}
              />
            </div>

            {config.farewellEnabled && (
              <div className="space-y-6 pt-2 animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2">
                  <Label>Canale</Label>
                  <Select
                    value={config.farewellChannelId}
                    onValueChange={(v) => setConfig({ ...config, farewellChannelId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona canale" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">Canale di sistema</SelectItem>
                      {channels.map((ch: any) => (
                        <SelectItem key={ch.id} value={ch.id}>#{ch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Contenuto messaggio</Label>
                  <Textarea
                    value={config.farewellMessage}
                    onChange={(e) => setConfig({ ...config, farewellMessage: e.target.value })}
                    placeholder="{user} ci ha lasciati..."
                    rows={3}
                    className="bg-black/20 font-mono text-sm"
                  />
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <Label>Invia come Embed</Label>
                  <Switch
                    checked={config.farewellSendEmbed ?? false}
                    onCheckedChange={(v) => setConfig({ ...config, farewellSendEmbed: v })}
                  />
                </div>

                {config.farewellSendEmbed && (
                  <>
                    {/* Discord Embed Preview Farewell */}
                    <DiscordEmbedPreview
                      title={config.farewellEmbedTitle}
                      description={config.farewellMessage.replace('{user}', 'User#1234').replace('{username}', 'User').replace('{server}', 'Server').replace('{memberCount}', '123')}
                      color={config.farewellEmbedColor}
                      thumbnail={
                        config.farewellEmbedThumbnail === 'server'
                          ? '/server-icon.png'
                          : config.farewellEmbedThumbnail === 'user'
                            ? '/user-avatar.png'
                            : undefined
                      }
                      image={config.farewellEmbedShowBanner && farewellBannerPreview ? farewellBannerPreview : undefined}
                      footer={`User left the server`}
                      username="Bot"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label>Titolo Embed</Label>
                        <Input
                          value={config.farewellEmbedTitle || ''}
                          onChange={(e) => setConfig({ ...config, farewellEmbedTitle: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Colore Embed</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={config.farewellEmbedColor || '#FF5555'}
                            onChange={(e) => setConfig({ ...config, farewellEmbedColor: e.target.value })}
                            className="w-12 h-10 p-1 bg-transparent border-0"
                          />
                          <Input
                            value={config.farewellEmbedColor || '#FF5555'}
                            onChange={(e) => setConfig({ ...config, farewellEmbedColor: e.target.value })}
                            className="font-mono"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Miniatura Embed</Label>
                        <Select
                          value={config.farewellEmbedThumbnail || 'user'}
                          onValueChange={(v) => setConfig({ ...config, farewellEmbedThumbnail: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Miniatura" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Avatar utente</SelectItem>
                            <SelectItem value="server">Icona server</SelectItem>
                            <SelectItem value="none">Nessuna</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Banner Embed</Label>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Mostra banner server</span>
                          <Switch
                            checked={!!config.farewellEmbedShowBanner}
                            onCheckedChange={(v) => setConfig({ ...config, farewellEmbedShowBanner: v })}
                          />
                        </div>
                      </div>

                      {/* Upload banner farewell */}
                      <div className="md:col-span-2 space-y-3 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                        <Label className="flex items-center gap-2 text-red-400">
                          <Upload className="w-4 h-4" />
                          Banner personalizzato addio
                        </Label>

                        {(config.farewellEmbedShowBanner && farewellBannerPreview && farewellBannerPreview.trim() !== '') ? (
                          <>
                            <div className="text-xs text-yellow-500 mt-1">Ricorda: il banner sarà usato solo dopo aver salvato la configurazione.</div>
                            <div className="relative group">
                              <img
                                src={farewellBannerPreview}
                                alt="Banner addio anteprima"
                                className="w-full h-32 object-cover rounded-lg border border-white/20"
                                onError={e => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  const fallback = document.getElementById('farewell-banner-fallback');
                                  if (fallback) fallback.style.display = 'block';
                                }}
                              />
                              <div className="text-xs text-yellow-500 mt-1">{t('welcome.bannerReminder')}</div>
                              <div id="farewell-banner-fallback" style={{display:'none'}} className="w-full h-32 flex items-center justify-center bg-red-100 text-red-500 rounded-lg border border-red-300">
                                {t('welcome.bannerError')}
                              </div>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => farewellFileInputRef.current?.click()}
                                  disabled={uploadingFarewell}
                                >
                                  {t('welcome.change')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setFarewellBannerPreview('');
                                    setConfig((p: any) => ({ ...p, farewellBannerImageUrl: '', farewellCustomBannerUrl: '' }));
                                    const fallback = document.getElementById('farewell-banner-fallback');
                                    if (fallback) fallback.style.display = 'none';
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full p-6 border-2 border-dashed border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors cursor-pointer text-center">
                            <ImageIcon className="w-8 h-8 text-red-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-red-400">
                              {t('welcome.noPreview')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('welcome.uploadHint')}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => farewellFileInputRef.current?.click()}
                              disabled={uploadingFarewell}
                              className="mt-2"
                            >
                              {t('welcome.uploadBannerBtn')}
                            </Button>
                          </div>
                        )}

                        <input
                          ref={farewellFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFarewellBannerUpload(file);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Pulsante Salva */}
        <div className="flex justify-end pt-6">
          <Button
            size="lg"
            onClick={handleSave}
            disabled={saving || !isEnabled}
            className="bg-indigo-600 hover:bg-indigo-700 min-w-[160px]"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('welcome.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('welcome.save')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}